const BOUND_ATTRIBUTE = 'data-swatches-bound';

// Matches the width the card's srcset candidates are generated at in product-card.liquid.
const DEFAULT_IMAGE_WIDTH = 660;

/**
 * Swaps a product card's image on interaction.
 *
 * Card hover shows the product's second image. Hovering or focusing a colour
 * swatch shows that variant's image and takes precedence over the card hover, so
 * moving off a swatch while still on the card falls back to the hover image.
 * Tapping a swatch on a touch device swaps the image and retargets the card's
 * links to that variant, so tapping the card afterwards opens the right one.
 *
 * The alternative images are carried as data attributes on the single <img>, so
 * there is only ever one image element per card.
 */
const initProductCardSwatches = () => {
  const cardEls = document.querySelectorAll(`[data-product-card]:not([${BOUND_ATTRIBUTE}])`);
  if (!cardEls.length) return;

  cardEls.forEach((cardEl) => {
    const imageEl = cardEl.querySelector('[data-card-image]');
    if (!imageEl) return;

    // Guards against re-binding when a section reloads in the theme editor, which
    // fires for every card on the page rather than just the reloaded section.
    cardEl.setAttribute(BOUND_ATTRIBUTE, '');

    const swatchEls = cardEl.querySelectorAll('[data-card-swatch]');
    const cardLinkEls = cardEl.querySelectorAll('[data-card-link]');

    const { cardHoverImage, cardHoverImageSrcset } = imageEl.dataset;

    // The image uses native lazy loading, so src holds a low-resolution placeholder
    // for the life of the element and the real candidates come from srcset. Both are
    // captured as rendered and restored together.
    const defaultSrc = imageEl.src;
    const defaultSrcset = imageEl.srcset;
    const defaultSizes = imageEl.sizes;

    // Set when a swatch is tapped, and outranks the card hover image until cleared.
    let selectedSrc = '';
    let selectedSrcset = '';

    const setImage = (src, srcset) => {
      if (!src) return;
      imageEl.src = src;
      imageEl.srcset = srcset || '';
      // Variant srcsets are narrower than the default, so pin sizes to the rendered
      // width while swapped to stop the browser picking an undersized candidate.
      // clientWidth is 0 for a card inside an inactive carousel slide, which would
      // otherwise resolve to the smallest candidate, so fall back to the intrinsic
      // width the srcset was generated at.
      const renderedWidth = imageEl.clientWidth || DEFAULT_IMAGE_WIDTH;
      imageEl.sizes = srcset ? `${renderedWidth}px` : defaultSizes;
    };

    const showDefaultImage = () => {
      imageEl.src = defaultSrc;
      imageEl.srcset = defaultSrcset;
      imageEl.sizes = defaultSizes;
    };

    const showHoverImage = () => {
      if (selectedSrc) {
        setImage(selectedSrc, selectedSrcset);
      } else if (cardHoverImage) {
        setImage(cardHoverImage, cardHoverImageSrcset);
      } else {
        showDefaultImage();
      }
    };

    const restoreImage = () => {
      if (selectedSrc) {
        setImage(selectedSrc, selectedSrcset);
      } else {
        showDefaultImage();
      }
    };

    // Pointer events rather than mouseenter/mouseleave, which mobile browsers
    // synthesise after a tap and would flash the hover image before navigating.
    if (cardHoverImage) {
      cardEl.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        showHoverImage();
      });
      cardEl.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        restoreImage();
      });
    }

    if (!swatchEls.length) return;

    // The restore is bound to the list rather than each swatch, so moving from one
    // swatch to the next never restores in between. The swatches themselves are
    // flush, but a wrapped row still leaves dead space the pointer can cross.
    const swatchListEl = cardEl.querySelector('[data-card-swatches]');

    const previewVariant = (swatchEl) => {
      const { variantImage, variantImageSrcset } = swatchEl.dataset;
      if (!variantImage) {
        showHoverImage();
        return;
      }
      setImage(variantImage, variantImageSrcset);
    };

    const selectVariant = (swatchEl) => {
      const { variantImage, variantImageSrcset, variantUrl } = swatchEl.dataset;

      selectedSrc = variantImage || '';
      selectedSrcset = variantImageSrcset || '';
      if (variantImage) setImage(variantImage, variantImageSrcset);

      swatchEls.forEach(el => el.removeAttribute('aria-current'));
      swatchEl.setAttribute('aria-current', 'true');

      if (variantUrl) cardLinkEls.forEach((linkEl) => { linkEl.href = variantUrl; });
    };

    swatchEls.forEach((swatchEl) => {
      // Recorded on pointerdown rather than read off the click event: click is only
      // a PointerEvent in newer browsers, and a keyboard-synthesised or programmatic
      // click carries no pointerType at all.
      let lastPointerType = '';

      // Branch per event rather than on a media query read once at init, so hybrid
      // devices behave correctly whichever input the shopper actually uses.
      swatchEl.addEventListener('pointerdown', (event) => {
        lastPointerType = event.pointerType;
      });

      swatchEl.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        previewVariant(swatchEl);
      });

      // Blur restores rather than falling back to the card hover image: a keyboard
      // user tabbing out is not hovering the card, so the default belongs there.
      swatchEl.addEventListener('focus', () => previewVariant(swatchEl));
      swatchEl.addEventListener('blur', restoreImage);

      // A tap selects the colour in place; the swatch still navigates for mouse and
      // keyboard users, so the variant page always remains reachable.
      swatchEl.addEventListener('click', (event) => {
        if (lastPointerType !== 'touch') return;
        event.preventDefault();
        selectVariant(swatchEl);
      });
    });

    if (swatchListEl) {
      swatchListEl.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        showHoverImage();
      });
    }
  });
};

export default initProductCardSwatches;
